/* eslint-disable react/jsx-no-bind */
import { useCallback, useEffect, useRef, useState } from "react";

import { track } from "~/lib/segmentAnalytics";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "~/components/shared/HoverCard";
import RemoteButtonComponent from "~/components/shared/RemoteButtonComponent";
import Spinner from "~/components/shared/Spinner";
import { Build, Job } from "~/components/build/Show/lib/types";
import { twMerge } from "tailwind-merge";
import { HoverCardPortal } from "@radix-ui/react-hover-card";
import Icon from "~/components/shared/Icon";

interface Props {
  build: {
    path: string;
    retryFailedJobsPath?: string;
    permissions: {
      retry: {
        allowed: boolean;
      };
    };
    canRetryFailedJobs: boolean;
    canRetryFailedTriggeredJobs: boolean;
    jobs?: Job[];
  };
  setBuildData: (build: Build) => void;
  className?: string;
  compactMode?: boolean;
  disableTooltip?: boolean;
}

/**
 * Custom hook to manage hover state manually since we're running into a bizarro issues with
 * the Radix HoverCard not properly handling mouse enter/leave events when this button is
 * rendered in some contexts.
 *
 * If we can resolve the issues with Radix, we can throw this out!
 */
const useManualHoverState = () => {
  const openRef = useRef<number>();
  const closeRef = useRef<number>();

  const [open, setOpen] = useState(false);
  const onMouseEnter = useCallback(() => {
    closeRef.current && clearTimeout(closeRef.current);
    closeRef.current = undefined;

    if (openRef.current) {
      return;
    }

    openRef.current = setTimeout(() => {
      setOpen(true);
    }, 300);
  }, []);

  const onMouseLeave = useCallback(() => {
    openRef.current && clearTimeout(openRef.current);
    openRef.current = undefined;

    closeRef.current = setTimeout(() => {
      setOpen(false);
      closeRef.current = undefined;
    }, 150);
  }, []);

  return { open, onMouseEnter, onMouseLeave };
};

export default function RetryFailedJobsButton({
  build,
  setBuildData,
  className,
  compactMode,
  disableTooltip,
}: Props) {
  // We set isLoading when the request starts, but we don't unset it once the
  // request finishes. We want to keep the spinner going until the build starts
  // running again, at which point this will be unrendered.
  const [isLoading, setIsLoading] = useState(false);
  const { open, onMouseEnter, onMouseLeave } = useManualHoverState();

  // When new jobs start, clear the loading state
  useEffect(() => {
    setIsLoading(false);
  }, [build.jobs?.length, build.canRetryFailedTriggeredJobs]);

  function handleBeforeStart() {
    setIsLoading(true);
    track("Retry Failed Jobs Clicked", {
      text: "Retry failed jobs",
      source: "Rebuild Dropdown",
    });
  }

  function handleSuccess(_event: Event, response: any) {
    if (response) {
      setBuildData(response);
    }
  }

  function handleError() {
    window.location.assign(build.path || "");
  }

  if (
    !build.permissions.retry.allowed ||
    (!build.canRetryFailedJobs && !build.canRetryFailedTriggeredJobs) ||
    !build.retryFailedJobsPath
  ) {
    return null;
  }

  return (
    <HoverCard open={open}>
      <HoverCardTrigger asChild={true} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <div>
          <RemoteButtonComponent
            url={build.retryFailedJobsPath}
            method="post"
            loading={isLoading}
            loadingText="Retrying jobs&hellip;"
            loadingIndicator={<Spinner />}
            className={twMerge(
              "btn btn-flex gap-1 text-inherit hover:text-inherit focus:text-inherit active:text-inherit",
              className,
            )}
            onBeforeStart={handleBeforeStart}
            onSuccess={handleSuccess}
            onError={handleError}
            title="Retry failed jobs"
          >
            {compactMode ? (
              <div className="flex items-center text-inherit">
                <Icon
                  icon="custom/outline/16x/arrows-refresh"
                  style={{ width: 16, height: 16 }}
                  className="mr-1"
                />
                <span>Retry failures</span>
              </div>
            ) : !compactMode && !build.canRetryFailedJobs && build.canRetryFailedTriggeredJobs ? (
              "Retry failed triggered jobs"
            ) : (
              "Retry failed jobs"
            )}
          </RemoteButtonComponent>
        </div>
      </HoverCardTrigger>

      {disableTooltip ? null : (
        <HoverCardPortal>
          <HoverCardContent
            size="max-lg"
            className="z-30"
            onMouseEnter={onMouseEnter}
            onMouseOut={onMouseLeave}
          >
            {!build.canRetryFailedJobs && build.canRetryFailedTriggeredJobs
              ? "Retry failed jobs in synchronous trigger builds"
              : "Retry failed jobs in this build, and in any synchronous trigger builds"}
          </HoverCardContent>
        </HoverCardPortal>
      )}
    </HoverCard>
  );
}
